<script setup lang="ts" generic="T">
import {
    VExpansionPanels, VExpansionPanel, VExpansionPanelTitle, VExpansionPanelText,
} from 'vuetify/components';

type PropsType<T> = {
    items: T[];
    multiple?: boolean;
    variant?: VExpansionPanels['$props']['variant'];
    elevation?: string | number;
    itemClasses?: string;
    panelErrors?: Record<number, boolean | string | number>;
};

const props = withDefaults(defineProps<PropsType<T>>(), {
    multiple: false,
    variant: 'accordion',
    elevation: 2,
});

const expandedPanels = defineModel<number[]>('expanded', { default: () => [] });

const hasErrors = (index: number) => {
    const error = props.panelErrors?.[index];

    return error !== undefined;
};

const expandPanel = (index: number) => {
    if (!expandedPanels.value.includes(index)) {
        if (props.multiple) {
            expandedPanels.value.push(index);
        } else {
            expandedPanels.value = [index];
        }
    }
};

const collapsePanel = (index: number) => {
    const indexToRemove = expandedPanels.value.indexOf(index);
    if (indexToRemove > -1) {
        expandedPanels.value.splice(indexToRemove, 1);
    }
};

const togglePanel = (index: number) => {
    if (expandedPanels.value.includes(index)) {
        collapsePanel(index);
    } else {
        expandPanel(index);
    }
};

defineExpose({
    expandPanel,
    collapsePanel,
    togglePanel,
    expandedPanels: expandedPanels.value,
});
defineOptions({ inheritAttrs: false });
</script>

<template>
    <VExpansionPanels
        v-model="expandedPanels"
        :multiple="multiple"
        :variant="variant"
        :elevation="elevation"
        class="b-expansion-panel"
        eager
        v-bind="$attrs"
    >
        <VExpansionPanel
            v-for="(item, index) in items"
            :key="index"
            :value="index"
            class="b-expansion-panel__item"
            :class="[itemClasses]"
            eager
        >
            <VExpansionPanelTitle
                class="b-expansion-panel__title"
                :class="{ 'b-expansion-panel__title--error': panelErrors?.[index] }"
            >
                <div class="b-expansion-panel__title-content">
                    <slot
                        name="title"
                        :item="item"
                        :index="index"
                        :expanded="expandedPanels.includes(index)"
                        :has-errors="hasErrors(index)"
                    >
                    </slot>
                </div>
            </VExpansionPanelTitle>

            <VExpansionPanelText class="b-expansion-panel__content">
                <slot
                    name="content"
                    :item="item"
                    :index="index"
                    :expanded="expandedPanels.includes(index)"
                >
                </slot>
            </VExpansionPanelText>
        </VExpansionPanel>
    </VExpansionPanels>
</template>

<style lang="scss" scoped>
.b-expansion-panel {
    &__title {
        &--error {
            color: rgb(var(--v-theme-error));
        }
    }

    &__title-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }

    &__error-indicator {
        display: flex;
        align-items: center;
    }
}
</style>
